/**
 * Integrations Redesign Service
 * Toggle USE_MOCK to switch between mock and real API.
 */

import type {
  CompanyWithIntegrations,
  SimpleIntegrationConfig,
  CompanyUser,
  EmployeeCompanyAccessRow,
  UpdateIntegrationsPayload,
  TestIntegrationResponse,
  AvailableDatabase,
} from '@/types/integrations-redesign';
import type { IntegrationConnectionStatus } from '@/types/company';
import { apiFetch } from '@/lib/api-error-handler';
import { getDataMode, getApiBaseUrl } from '@/lib/data-service';

// Default config used to fill in any missing integration fields from the API
const NOT_CONFIGURED: SimpleIntegrationConfig = {
  product: null,
  databaseId: null,
  status: 'not_configured',
  lastSyncAt: null,
};

/**
 * Ensure every integration field on a company object is present.
 * The backend may omit fields (e.g. purchasingIntegration) that the
 * frontend expects, so we fill in a safe default for any that are missing.
 */
function normaliseCompany(c: CompanyWithIntegrations): CompanyWithIntegrations {
  return {
    ...c,
    financeIntegration: c.financeIntegration ?? { ...NOT_CONFIGURED },
    timesheetIntegration: c.timesheetIntegration ?? { ...NOT_CONFIGURED },
    expenseIntegration: c.expenseIntegration ?? { ...NOT_CONFIGURED },
    plannerIntegration: c.plannerIntegration ?? { ...NOT_CONFIGURED },
    purchasingIntegration: c.purchasingIntegration ?? { ...NOT_CONFIGURED },
  };
}

// ---------------------------------------------------------------------------
// Mock Database Catalogue
// These represent the databases discoverable on the connected server.
// In production this would be fetched from the API.
// ---------------------------------------------------------------------------

const MOCK_DATABASES: Record<string, AvailableDatabase[]> = {
  // Finance products
  'access-financials': [
    { id: 'FIN-DB-001', label: 'Contoso Finance', environment: 'Production' },
    { id: 'FIN-DB-004', label: 'Adventure Works Finance', environment: 'Production' },
    { id: 'FIN-DB-UAT', label: 'UAT Finance DB', environment: 'UAT' },
    { id: 'FIN-DB-STG', label: 'Staging Finance DB', environment: 'Staging' },
  ],
  'access-dimensions': [
    { id: 'DIM-DB-002', label: 'Fabrikam Dimensions', environment: 'Production' },
    { id: 'DIM-DB-006', label: 'Wide World Dimensions', environment: 'Production' },
    { id: 'DIM-DB-UAT', label: 'UAT Dimensions DB', environment: 'UAT' },
  ],
  // Timesheet products
  'access-time': [
    { id: 'TS-DB-001', label: 'Contoso Timesheets', environment: 'Production' },
    { id: 'TS-DB-004', label: 'Adventure Works Timesheets', environment: 'Production' },
    { id: 'TS-DB-UAT', label: 'UAT Timesheets DB', environment: 'UAT' },
  ],
  // Expense products
  'access-expense': [
    { id: 'EXP-DB-001', label: 'Contoso Expenses', environment: 'Production' },
    { id: 'EXP-DB-002', label: 'Fabrikam Expenses', environment: 'Production' },
    { id: 'EXP-DB-UAT', label: 'UAT Expense DB', environment: 'UAT' },
  ],
  // Planner products — shows available companies (not raw database IDs)
  'access-planner': [
    { id: 'PLN-DB-001', label: 'Contoso Ltd', environment: 'Production' },
    { id: 'PLN-DB-002', label: 'Fabrikam Inc', environment: 'Production' },
    { id: 'PLN-DB-003', label: 'Northwind Traders', environment: 'Production' },
    { id: 'PLN-DB-UAT', label: 'UAT Planner', environment: 'UAT' },
  ],
  'resource-scheduler': [
    { id: 'RS-DB-001', label: 'Contoso Ltd', environment: 'Production' },
    { id: 'RS-DB-004', label: 'Adventure Works', environment: 'Production' },
    { id: 'RS-DB-UAT', label: 'UAT Scheduler', environment: 'UAT' },
  ],
  // Timesheet - FocalPoint / Financial Operations
  'access-focalpoint': [
    { id: 'TS-FP-002', label: 'Fabrikam FocalPoint', environment: 'Production' },
    { id: 'TS-FP-UAT', label: 'UAT FocalPoint DB', environment: 'UAT' },
  ],
  'access-financial-operations': [
    { id: 'TS-FO-006', label: 'Wide World Fin Ops', environment: 'Production' },
    { id: 'TS-FO-UAT', label: 'UAT Fin Ops DB', environment: 'UAT' },
  ],
  // Purchasing products (keyed by PurchasingProduct values)
  // For 'access-financial-operations-purchasing' the dropdown shows Financials instances.
  // Behind the scenes the matching FinOps instance is resolved by name (see MOCK_FINOPS_BY_FINANCIALS_NAME).
  'access-financial-operations-purchasing': [
    { id: 'cce88d34-9533-4eb2-bb45-0259dabc58b0', label: 'FinOps01', environment: 'Production' },
    { id: 'fin-inst-002', label: 'FinOps02', environment: 'Production' },
    { id: 'fin-inst-uat', label: 'FinOps UAT', environment: 'UAT' },
  ],
  'access-focalpoint-purchasing': [
    { id: 'PUR-FP-002', label: 'Fabrikam FocalPoint Purchasing', environment: 'Production' },
    { id: 'PUR-FP-UAT', label: 'UAT FocalPoint Purchasing', environment: 'UAT' },
  ],
  // Fallback for 'other' product type
  other: [
    { id: 'OTHER-001', label: 'Other DB 1', environment: 'Production' },
    { id: 'OTHER-002', label: 'Other DB 2', environment: 'Staging' },
  ],
};

// ---------------------------------------------------------------------------
// Mock FinOps instance lookup (by Financials instance name)
// In production the backend calls the Workspace API with the
// 'access-financials-operations' platform product ID and matches by name.
// ---------------------------------------------------------------------------

const MOCK_FINOPS_BY_FINANCIALS_NAME: Record<string, { id: string; name: string }> = {
  'FinOps01': { id: '9ab3b82a-a3b1-45db-bd46-b69d5b8cb994', name: 'FinOps01' },
  'FinOps02': { id: 'finops-inst-002', name: 'FinOps02' },
  'FinOps UAT': { id: 'finops-inst-uat', name: 'FinOps UAT' },
};

// ---------------------------------------------------------------------------
// Mock In-Memory Store
// ---------------------------------------------------------------------------

function makeIntegration(
  product: string | null,
  databaseId: string | null,
  status: IntegrationConnectionStatus,
  lastSyncAt: string | null,
) {
  return { product, databaseId, status, lastSyncAt } as CompanyWithIntegrations['financeIntegration'];
}

const INITIAL_MOCK_COMPANIES: CompanyWithIntegrations[] = [
  {
    id: 1,
    name: 'Contoso Ltd',
    code: 'CTL',
    status: 'active',
    financeIntegration: makeIntegration('access-financials', 'FIN-DB-001', 'connected', new Date(Date.now() - 3600000).toISOString()),
    timesheetIntegration: makeIntegration('access-time', 'TS-DB-001', 'connected', new Date(Date.now() - 7200000).toISOString()),
    expenseIntegration: makeIntegration('access-expense', 'EXP-DB-001', 'not_connected', null),
    plannerIntegration: makeIntegration(null, null, 'not_configured', null),
    purchasingIntegration: {
      ...makeIntegration('access-financial-operations-purchasing', 'cce88d34-9533-4eb2-bb45-0259dabc58b0', 'connected', new Date(Date.now() - 5400000).toISOString()),
      finOpsInstanceId: '9ab3b82a-a3b1-45db-bd46-b69d5b8cb994',
    },
    userCount: 14,
  },
  {
    id: 2,
    name: 'Fabrikam Inc',
    code: 'FAB',
    status: 'active',
    financeIntegration: makeIntegration('access-dimensions', 'DIM-DB-002', 'connected', new Date(Date.now() - 86400000).toISOString()),
    timesheetIntegration: makeIntegration('access-time', 'TS-DB-001', 'connected', new Date(Date.now() - 86400000).toISOString()),
    expenseIntegration: makeIntegration('access-expense', 'EXP-DB-002', 'connected', new Date(Date.now() - 3600000).toISOString()),
    plannerIntegration: makeIntegration('access-planner', 'PLN-DB-002', 'connected', new Date(Date.now() - 900000).toISOString()),
    purchasingIntegration: makeIntegration('access-focalpoint-purchasing', 'PUR-FP-002', 'connected', new Date(Date.now() - 3600000).toISOString()),
    userCount: 8,
  },
  {
    id: 3,
    name: 'Northwind Traders',
    code: 'NWT',
    status: 'active',
    financeIntegration: makeIntegration(null, null, 'not_configured', null),
    timesheetIntegration: makeIntegration(null, null, 'not_configured', null),
    expenseIntegration: makeIntegration(null, null, 'not_configured', null),
    plannerIntegration: makeIntegration(null, null, 'not_configured', null),
    purchasingIntegration: makeIntegration(null, null, 'not_configured', null),
    userCount: 3,
  },
  {
    id: 4,
    name: 'Adventure Works',
    code: 'ADW',
    status: 'active',
    financeIntegration: makeIntegration('access-financials', 'FIN-DB-004', 'connected', new Date(Date.now() - 1800000).toISOString()),
    timesheetIntegration: makeIntegration('access-time', 'TS-DB-004', 'not_connected', null),
    expenseIntegration: makeIntegration(null, null, 'not_configured', null),
    plannerIntegration: makeIntegration('resource-scheduler', 'RS-DB-004', 'connected', new Date(Date.now() - 600000).toISOString()),
    purchasingIntegration: makeIntegration(null, null, 'not_configured', null),
    userCount: 22,
  },
  {
    id: 5,
    name: 'Tailspin Toys',
    code: 'TST',
    status: 'inactive',
    financeIntegration: makeIntegration('other', 'OTHER-001', 'not_connected', null),
    timesheetIntegration: makeIntegration(null, null, 'not_configured', null),
    expenseIntegration: makeIntegration(null, null, 'not_configured', null),
    plannerIntegration: makeIntegration(null, null, 'not_configured', null),
    purchasingIntegration: makeIntegration(null, null, 'not_configured', null),
    userCount: 1,
  },
  {
    id: 6,
    name: 'Wide World Importers',
    code: 'WWI',
    status: 'inactive',
    financeIntegration: makeIntegration('access-dimensions', 'DIM-DB-006', 'error', new Date(Date.now() - 604800000).toISOString()),
    timesheetIntegration: makeIntegration('access-time', 'TS-DB-001', 'not_connected', null),
    expenseIntegration: makeIntegration(null, null, 'not_configured', null),
    plannerIntegration: makeIntegration(null, null, 'not_configured', null),
    purchasingIntegration: makeIntegration(null, null, 'not_configured', null),
    userCount: 0,
  },
];

const MOCK_USERS: CompanyUser[] = [
  { id: 'u1', name: 'Alice Johnson', email: 'alice@contoso.com', role: 'Admin', isDefault: false },
  { id: 'u2', name: 'Bob Smith', email: 'bob@contoso.com', role: 'Manager', isDefault: false },
  { id: 'u3', name: 'Carol White', email: 'carol@contoso.com', role: 'User', isDefault: false },
  { id: 'u4', name: 'David Brown', email: 'david@fabrikam.com', role: 'Manager', isDefault: false },
  { id: 'u5', name: 'Eva Martinez', email: 'eva@fabrikam.com', role: 'User', isDefault: false },
  { id: 'u6', name: 'Frank Lee', email: 'frank@northwind.com', role: 'Admin', isDefault: false },
  { id: 'u7', name: 'Grace Kim', email: 'grace@adventureworks.com', role: 'User', isDefault: false },
  { id: 'u8', name: 'Henry Zhang', email: 'henry@adventureworks.com', role: 'User', isDefault: false },
];

// company id -> employee ids (must match Employee.id values used in the Resource Directory)
const mockCompanyUsers: Record<number, string[]> = {
  1: ['1', '2', '3', '4', '5', '6', '7', '9', '10', '14', '17'],
  2: ['1', '3', '8', '11', '12', '15', '16', '20'],
  3: ['2', '5', '6', '9', '13', '18', '19'],
  4: ['4', '7', '10', '14', '21', '22', '23'],
  5: [],
  6: [],
};

// Mutable copy so mutations persist within the session
let mockCompanies: CompanyWithIntegrations[] = JSON.parse(JSON.stringify(INITIAL_MOCK_COMPANIES));

function delay(ms = 400): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

function cloneCompanies(): CompanyWithIntegrations[] {
  return JSON.parse(JSON.stringify(mockCompanies));
}

// ---------------------------------------------------------------------------
// Mock Service
// ---------------------------------------------------------------------------

const mockIntegrationsService = {
  async getCompanies(): Promise<CompanyWithIntegrations[]> {
    await delay();
    return cloneCompanies();
  },

  async getCompany(id: number): Promise<CompanyWithIntegrations | undefined> {
    await delay(200);
    return cloneCompanies().find(c => c.id === id);
  },

  async createCompany(data: { name: string; code: string; status: 'active' | 'inactive' }): Promise<CompanyWithIntegrations> {
    await delay(500);
    const newCompany: CompanyWithIntegrations = {
      id: Date.now(),
      name: data.name,
      code: data.code,
      status: data.status,
      financeIntegration: makeIntegration(null, null, 'not_configured', null),
      timesheetIntegration: makeIntegration(null, null, 'not_configured', null),
      expenseIntegration: makeIntegration(null, null, 'not_configured', null),
      plannerIntegration: makeIntegration(null, null, 'not_configured', null),
      purchasingIntegration: makeIntegration(null, null, 'not_configured', null),
      userCount: 0,
    };
    mockCompanies.push(newCompany);
    mockCompanyUsers[newCompany.id] = [];
    return JSON.parse(JSON.stringify(newCompany));
  },

  async updateCompany(
    id: number,
    data: Partial<Pick<CompanyWithIntegrations, 'name' | 'code' | 'status'>>,
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(400);
    const idx = mockCompanies.findIndex(c => c.id === id);
    if (idx === -1) return undefined;
    mockCompanies[idx] = { ...mockCompanies[idx], ...data };
    return JSON.parse(JSON.stringify(mockCompanies[idx]));
  },

  async updateIntegrations(
    companyId: number,
    payload: UpdateIntegrationsPayload,
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(500);
    const idx = mockCompanies.findIndex(c => c.id === companyId);
    if (idx === -1) return undefined;
    const company = mockCompanies[idx];

    const updateSection = (
      current: CompanyWithIntegrations['financeIntegration'],
      update: { product: string | null; databaseId: string | null; finOpsInstanceId?: string | null } | undefined,
    ): CompanyWithIntegrations['financeIntegration'] => {
      // If no update provided for this section, keep current config unchanged
      if (!update) return current;
      const hasConfig = update.product && update.databaseId;
      return {
        product: update.product as CompanyWithIntegrations['financeIntegration']['product'],
        databaseId: update.databaseId,
        finOpsInstanceId: update.finOpsInstanceId ?? current.finOpsInstanceId ?? null,
        status: hasConfig ? 'not_connected' : 'not_configured',
        lastSyncAt: current.lastSyncAt,
      };
    };

    mockCompanies[idx] = {
      ...company,
      financeIntegration: updateSection(company.financeIntegration, payload.finance),
      timesheetIntegration: updateSection(company.timesheetIntegration, payload.timesheet),
      expenseIntegration: updateSection(company.expenseIntegration, payload.expense),
      plannerIntegration: updateSection(company.plannerIntegration, payload.planner),
      purchasingIntegration: updateSection(company.purchasingIntegration, payload.purchasing),
    };

    return JSON.parse(JSON.stringify(mockCompanies[idx]));
  },

  async syncIntegration(
    companyId: number,
    type: 'finance' | 'timesheet' | 'expense' | 'planner' | 'purchasing',
  ): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
    await delay(1200);
    const idx = mockCompanies.findIndex(c => c.id === companyId);
    if (idx === -1) return { success: false, message: 'Company not found', syncedAt: null, updatedCompany: undefined };
    const key = `${type}Integration` as keyof CompanyWithIntegrations;
    const now = new Date().toISOString();
    (mockCompanies[idx][key] as CompanyWithIntegrations['financeIntegration']).lastSyncAt = now;
    (mockCompanies[idx][key] as CompanyWithIntegrations['financeIntegration']).status = 'connected';
    return {
      success: true,
      message: 'Sync completed successfully',
      syncedAt: now,
      updatedCompany: JSON.parse(JSON.stringify(mockCompanies[idx])),
    };
  },

  async removeIntegration(
    companyId: number,
    type: 'finance' | 'timesheet' | 'expense' | 'planner' | 'purchasing',
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(400);
    const idx = mockCompanies.findIndex(c => c.id === companyId);
    if (idx === -1) return undefined;
    const key = `${type}Integration` as keyof CompanyWithIntegrations;
    (mockCompanies[idx][key] as CompanyWithIntegrations['financeIntegration']) = makeIntegration(null, null, 'not_configured', null);
    return JSON.parse(JSON.stringify(mockCompanies[idx]));
  },

  async testConnection(
    companyId: number,
    type: 'finance' | 'timesheet' | 'expense' | 'planner' | 'purchasing',
  ): Promise<TestIntegrationResponse> {
    await delay(1000);
    const idx = mockCompanies.findIndex(c => c.id === companyId);
    if (idx === -1) return { success: false, message: 'Company not found', testedAt: new Date().toISOString() };
    const key = `${type}Integration` as keyof CompanyWithIntegrations;
    const integration = mockCompanies[idx][key] as CompanyWithIntegrations['financeIntegration'];
    if (integration.product && integration.databaseId) {
      integration.status = 'connected';
    }
    return {
      success: true,
      message: 'Connection test successful',
      testedAt: new Date().toISOString(),
    };
  },

  async getCompanyUsers(companyId: number): Promise<CompanyUser[]> {
    await delay(300);
    const userIds = mockCompanyUsers[companyId] ?? [];
    return MOCK_USERS.filter(u => userIds.includes(u.id)).map(u => JSON.parse(JSON.stringify(u)));
  },

  async addUserToCompany(companyId: number, userId: string): Promise<CompanyUser | undefined> {
    await delay(300);
    if (!mockCompanyUsers[companyId]) mockCompanyUsers[companyId] = [];
    if (!mockCompanyUsers[companyId].includes(userId)) {
      mockCompanyUsers[companyId].push(userId);
      // Update userCount
      const idx = mockCompanies.findIndex(c => c.id === companyId);
      if (idx !== -1) mockCompanies[idx].userCount = mockCompanyUsers[companyId].length;
    }
    return MOCK_USERS.find(u => u.id === userId);
  },

  async removeUserFromCompany(companyId: number, userId: string): Promise<void> {
    await delay(300);
    if (mockCompanyUsers[companyId]) {
      mockCompanyUsers[companyId] = mockCompanyUsers[companyId].filter(id => id !== userId);
      const idx = mockCompanies.findIndex(c => c.id === companyId);
      if (idx !== -1) mockCompanies[idx].userCount = mockCompanyUsers[companyId].length;
    }
  },

  async searchUsers(query: string, excludeCompanyId?: number): Promise<CompanyUser[]> {
    await delay(200);
    const q = query.toLowerCase();
    const excludedIds = excludeCompanyId ? (mockCompanyUsers[excludeCompanyId] ?? []) : [];
    return MOCK_USERS.filter(
      u =>
        !excludedIds.includes(u.id) &&
        (u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)),
    ).map(u => JSON.parse(JSON.stringify(u)));
  },

  async getAvailableDatabases(product: string): Promise<AvailableDatabase[]> {
    await delay(300);
    return MOCK_DATABASES[product] ?? [];
  },

  async getFinOpsInstanceByName(name: string): Promise<{ id: string; name: string } | null> {
    await delay(200);
    return MOCK_FINOPS_BY_FINANCIALS_NAME[name] ?? null;
  },

  async getPlannerCompanies(_instanceId: string): Promise<AvailableDatabase[]> {
    await delay(300);
    // In mock mode, return the planner mock databases regardless of instanceId
    return MOCK_DATABASES['access-planner'] ?? [];
  },

  // Mock sync status: simulates "in-progress" for the first few calls, then "completed"
  _syncStatusCallCounts: {} as Record<number, number>,
  async getSyncStatus(companyId: number): Promise<{ status: string; message: string }> {
    await delay(500);
    const count = (this._syncStatusCallCounts[companyId] ?? 0) + 1;
    this._syncStatusCallCounts[companyId] = count;
    if (count <= 2) {
      return { status: 'in-progress', message: 'Sync is running…' };
    }
    // Reset counter for next sync simulation
    delete this._syncStatusCallCounts[companyId];
    return { status: 'completed', message: 'Sync completed successfully' };
  },

  async getEmployeeCompanyAccess(employeeId: string): Promise<EmployeeCompanyAccessRow[]> {
    await delay(300);
    return cloneCompanies().map(c => ({
      companyId: c.id,
      companyName: c.name,
      companyCode: c.code,
      companyStatus: c.status,
      hasAccess: (mockCompanyUsers[c.id] ?? []).includes(employeeId),
      isDefault: false,
    }));
  },

  async updateEmployeeCompanyAccess(
    employeeId: string,
    updates: Array<{ companyId: number; hasAccess: boolean; isDefault: boolean }>,
  ): Promise<EmployeeCompanyAccessRow[]> {
    await delay(400);
    for (const u of updates) {
      if (!mockCompanyUsers[u.companyId]) mockCompanyUsers[u.companyId] = [];
      if (u.hasAccess && !mockCompanyUsers[u.companyId].includes(employeeId)) {
        mockCompanyUsers[u.companyId].push(employeeId);
      } else if (!u.hasAccess) {
        mockCompanyUsers[u.companyId] = mockCompanyUsers[u.companyId].filter(id => id !== employeeId);
      }
    }
    return mockIntegrationsService.getEmployeeCompanyAccess(employeeId);
  },
};

// ---------------------------------------------------------------------------
// Real API Service (stub — implement when USE_MOCK is false)
// ---------------------------------------------------------------------------

const realIntegrationsService = {
  async getCompanies(): Promise<CompanyWithIntegrations[]> {
    const companies = await apiFetch<CompanyWithIntegrations[]>(`${getApiBaseUrl()}/companies/with-integrations`, {
      credentials: 'include',
    });
    return companies.map(normaliseCompany);
  },

  async getCompany(id: number): Promise<CompanyWithIntegrations | undefined> {
    const company = await apiFetch<CompanyWithIntegrations>(`${getApiBaseUrl()}/companies/${id}/with-integrations`, {
      credentials: 'include',
    });
    return company ? normaliseCompany(company) : undefined;
  },

  async createCompany(data: { name: string; code: string; status: 'active' | 'inactive' }): Promise<CompanyWithIntegrations> {
    const company = await apiFetch<CompanyWithIntegrations>(`${getApiBaseUrl()}/companies/with-integrations`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return normaliseCompany(company);
  },

  async updateCompany(
    id: number,
    data: Partial<Pick<CompanyWithIntegrations, 'name' | 'code' | 'status'>>,
  ): Promise<CompanyWithIntegrations | undefined> {
    const company = await apiFetch<CompanyWithIntegrations>(`${getApiBaseUrl()}/companies/${id}/with-integrations`, {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return company ? normaliseCompany(company) : undefined;
  },

  async updateIntegrations(
    companyId: number,
    payload: UpdateIntegrationsPayload,
  ): Promise<CompanyWithIntegrations | undefined> {
    const company = await apiFetch<CompanyWithIntegrations>(`${getApiBaseUrl()}/companies/${companyId}/integrations`, {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    return company ? normaliseCompany(company) : undefined;
  },

  async syncIntegration(
    companyId: number,
    type: 'finance' | 'timesheet' | 'expense' | 'planner' | 'purchasing',
  ): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
    const result = await apiFetch<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }>(`${getApiBaseUrl()}/companies/${companyId}/integrations/${type}/sync`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ type }),
    });
    return {
      ...result,
      updatedCompany: result.updatedCompany ? normaliseCompany(result.updatedCompany) : undefined,
    };
  },

  async removeIntegration(
    companyId: number,
    type: 'finance' | 'timesheet' | 'expense' | 'planner' | 'purchasing',
  ): Promise<CompanyWithIntegrations | undefined> {
    const company = await apiFetch<CompanyWithIntegrations>(`${getApiBaseUrl()}/companies/${companyId}/integrations/${type}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return company ? normaliseCompany(company) : undefined;
  },

  async testConnection(
    companyId: number,
    type: 'finance' | 'timesheet' | 'expense' | 'planner' | 'purchasing',
  ): Promise<TestIntegrationResponse> {
    return apiFetch<TestIntegrationResponse>(`${getApiBaseUrl()}/companies/${companyId}/integrations/${type}/test`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ type }),
    });
  },

  async getCompanyUsers(companyId: number): Promise<CompanyUser[]> {
    return apiFetch<CompanyUser[]>(`${getApiBaseUrl()}/companies/${companyId}/users`, {
      credentials: 'include',
    });
  },

  async addUserToCompany(companyId: number, userId: string): Promise<CompanyUser | undefined> {
    return apiFetch<CompanyUser>(`${getApiBaseUrl()}/companies/${companyId}/users`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ userId: Number(userId) }),
    });
  },

  async removeUserFromCompany(companyId: number, userId: string): Promise<void> {
    await apiFetch(`${getApiBaseUrl()}/companies/${companyId}/users/${userId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  },

  async searchUsers(query: string, excludeCompanyId?: number): Promise<CompanyUser[]> {
    const companyId = excludeCompanyId ?? 0;
    const params = new URLSearchParams({ q: query });
    return apiFetch<CompanyUser[]>(`${getApiBaseUrl()}/companies/${companyId}/users/search?${params}`, {
      credentials: 'include',
    });
  },

  async getAvailableDatabases(product: string): Promise<AvailableDatabase[]> {
    const params = new URLSearchParams({ product });
    return apiFetch<AvailableDatabase[]>(`${getApiBaseUrl()}/integrations/databases?${params}`, {
      credentials: 'include',
    });
  },

  async getFinOpsInstanceByName(name: string): Promise<{ id: string; name: string } | null> {
    const params = new URLSearchParams({ name });
    return apiFetch<{ id: string; name: string } | null>(`${getApiBaseUrl()}/integrations/finops-instance?${params}`, {
      credentials: 'include',
    });
  },

  async getPlannerCompanies(instanceId: string): Promise<AvailableDatabase[]> {
    const params = new URLSearchParams({ instanceId });
    return apiFetch<AvailableDatabase[]>(`${getApiBaseUrl()}/integrations/planner/companies?${params}`, {
      credentials: 'include',
    });
  },

  async getSyncStatus(companyId: number): Promise<{ status: string; message: string }> {
    return apiFetch<{ status: string; message: string }>(`${getApiBaseUrl()}/companies/${companyId}/sync/finops/status`, {
      credentials: 'include',
    });
  },

  async getEmployeeCompanyAccess(employeeId: string): Promise<EmployeeCompanyAccessRow[]> {
    return apiFetch<EmployeeCompanyAccessRow[]>(`${getApiBaseUrl()}/employees/${employeeId}/company-access`, {
      credentials: 'include',
    });
  },

  async updateEmployeeCompanyAccess(
    employeeId: string,
    updates: Array<{ companyId: number; hasAccess: boolean; isDefault: boolean }>,
  ): Promise<EmployeeCompanyAccessRow[]> {
    return apiFetch<EmployeeCompanyAccessRow[]>(`${getApiBaseUrl()}/employees/${employeeId}/company-access`, {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(updates),
    });
  },
};

// ---------------------------------------------------------------------------
// Exported Service
// Dynamically selects mock or real service based on the current data mode.
// Demo mode → mock service, Live mode → real API service.
// ---------------------------------------------------------------------------

function getActiveService() {
  const mode = getDataMode();
  return mode === 'live' ? realIntegrationsService : mockIntegrationsService;
}

export const getCompanies: typeof mockIntegrationsService.getCompanies = (...args) =>
  getActiveService().getCompanies(...args);
export const getCompany: typeof mockIntegrationsService.getCompany = (...args) =>
  getActiveService().getCompany(...args);
export const createCompany: typeof mockIntegrationsService.createCompany = (...args) =>
  getActiveService().createCompany(...args);
export const updateCompany: typeof mockIntegrationsService.updateCompany = (...args) =>
  getActiveService().updateCompany(...args);
export const updateIntegrations: typeof mockIntegrationsService.updateIntegrations = (...args) =>
  getActiveService().updateIntegrations(...args);
export const syncIntegration: typeof mockIntegrationsService.syncIntegration = (...args) =>
  getActiveService().syncIntegration(...args);
export const removeIntegration: typeof mockIntegrationsService.removeIntegration = (...args) =>
  getActiveService().removeIntegration(...args);
export const testConnection: typeof mockIntegrationsService.testConnection = (...args) =>
  getActiveService().testConnection(...args);
export const getCompanyUsers: typeof mockIntegrationsService.getCompanyUsers = (...args) =>
  getActiveService().getCompanyUsers(...args);
export const addUserToCompany: typeof mockIntegrationsService.addUserToCompany = (...args) =>
  getActiveService().addUserToCompany(...args);
export const removeUserFromCompany: typeof mockIntegrationsService.removeUserFromCompany = (...args) =>
  getActiveService().removeUserFromCompany(...args);
export const searchUsers: typeof mockIntegrationsService.searchUsers = (...args) =>
  getActiveService().searchUsers(...args);
export const getAvailableDatabases: typeof mockIntegrationsService.getAvailableDatabases = (...args) =>
  getActiveService().getAvailableDatabases(...args);
export const getFinOpsInstanceByName: typeof mockIntegrationsService.getFinOpsInstanceByName = (...args) =>
  getActiveService().getFinOpsInstanceByName(...args);
export const getPlannerCompanies: typeof mockIntegrationsService.getPlannerCompanies = (...args) =>
  getActiveService().getPlannerCompanies(...args);
export const getSyncStatus: typeof mockIntegrationsService.getSyncStatus = (...args) =>
  getActiveService().getSyncStatus(...args);
export const getEmployeeCompanyAccess: typeof mockIntegrationsService.getEmployeeCompanyAccess = (...args) =>
  getActiveService().getEmployeeCompanyAccess(...args);
export const updateEmployeeCompanyAccess: typeof mockIntegrationsService.updateEmployeeCompanyAccess = (...args) =>
  getActiveService().updateEmployeeCompanyAccess(...args);

// ---------------------------------------------------------------------------
// Current User Company Access
// ---------------------------------------------------------------------------

/**
 * Get the company access list for the currently authenticated user.
 * In live mode hits GET /api/employees/me/company-access.
 * In demo mode falls back to the mock service with 'me' as the employee id.
 */
export async function getCurrentUserCompanyAccess(): Promise<EmployeeCompanyAccessRow[]> {
  const mode = getDataMode();
  if (mode === 'live') {
    return apiFetch<EmployeeCompanyAccessRow[]>(`${getApiBaseUrl()}/employees/me/company-access`, {
      credentials: 'include',
    });
  }
  return mockIntegrationsService.getEmployeeCompanyAccess('me');
}

export default { getActiveService };
